import { computed, ref, type Ref } from 'vue'
import { defineStore } from 'pinia'

import { getTemplateData, getUrlParam, setUrlParam } from '@/utils'

import type { Product } from '@/types/shopify'

export const PRODUCT_FILTERS = {
  'all': 'All Flavors',
  'new-flavor': 'New Flavors',
  'seasonal': 'Seasonal',
  'fruit-flavor': 'Fruit',
  'caffeinated': 'Caffeinated',
  'vegan': 'Vegan',
  'whey-protein': 'Dairy',
  'no-peanuts': 'No Peanuts',
  'no-tree-nuts': 'No Tree Nuts'
  // ,
  // 'seasonal': 'Seasonal'
} as const

export type ProductFilter = keyof typeof PRODUCT_FILTERS

const getFilterUrl = () => {
  const value = getUrlParam('filter') as ProductFilter
  if (value && Object.keys(PRODUCT_FILTERS).includes(value)) {
    return value
  }
  return 'all'
}

const setFilterUrl = (value: ProductFilter) => setUrlParam('filter', value)

export const useProductsStore = defineStore('products', () => {

  const loadProducts = () => {
    const data = (getTemplateData<Product[]>('#json-products') || [])
    const productsMeta = getTemplateData<Record<string, Partial<Product>>>('#json-products-meta') || {}
    return data.map((product) => {
      return {
        ...product,
        ...productsMeta[product.id]
      } as Product
    })
  }

  const loadProduct = () => {
    const data = getTemplateData<Product>('#json-product')
    if (!data) {
      return
    }
    const productMeta = getTemplateData<Partial<Product>>('#json-product-meta') || {}
    return {
      ...data,
      ...productMeta
    } as Product
  }

  const filter = ref<ProductFilter>(getFilterUrl())
  const productList: Ref<Product[]> = ref<Product[]>(loadProducts())
  const product = ref<Product | undefined>(loadProduct())

  // const recentProductsIds = ref<number>()

  const applyFilter = (products: Product[], value: ProductFilter) => {
    if (value === 'all') {
      return products
    } else if (value.startsWith('no-')) {
      const tag = value.replace('no-', '')
      return products.filter(i => !i.tags.includes(tag))
    } else {
      return products.filter(i => i.tags.includes(value))
    }
  }

  const filteredProducts = computed(() => {
    const products = productList.value.filter(product => !product.tags.includes('hidden'))
    return applyFilter(products, filter.value)
  })

  const availableFilters = computed(() => {
    const products = productList.value.filter(product => !product.tags.includes('hidden'))
    return Object.fromEntries(
      Object.entries(PRODUCT_FILTERS).filter(([ option ]) =>
        applyFilter(products, option as ProductFilter).length > 0
      )
    ) as Partial<typeof PRODUCT_FILTERS>
  })

  const ensureValidFilter = () => {
    const urlFilter = getFilterUrl()
    if (filter.value !== urlFilter) {
      filter.value = urlFilter
    }

    const { tags } = product.value || {}
    if (tags && !tags.includes(filter.value)) {
      filter.value = 'all'
    }
  }

  // const handleRecentProducts = () => {
  //   if (product.value) {
  //     const { id } = product.value
  //     const { value } = recentProductsIds
  //     const index = value.indexOf(id)
  //     if (index >= 0) {
  //       value.unshift(value.splice(index, 1)[0])
  //     } else {
  //       value.unshift(id)
  //     }

  //     if (value.length > 8) {
  //       value.pop()
  //     }
  //   }
  // }

  return {
    ensureValidFilter,
    setFilterUrl,
    filter,
    productList,
    filteredProducts,
    availableFilters,
    product
  }
}, {
  persist: {
    storage: window.sessionStorage,
    pick: [
      'filter'
    ],
    afterHydrate: ({ store }) => store.ensureValidFilter()
  }
})

